import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, api } from '../api';
import { getBranding } from '../branding';
import type { User } from '../types';
import { Brand, BrandLinks } from './Brand';
import { Icon } from './Icon';

export function MagicLinkSignIn({ token, currentUser, onAuthenticated, onCancel, browserTrustEnabled = false }: {
  token: string; currentUser: User | null;
  onAuthenticated: (user: User) => void; onCancel: () => void;
  browserTrustEnabled?: boolean;
}) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [canTrustBrowser, setCanTrustBrowser] = useState(false);
  const [trustBrowser, setTrustBrowser] = useState(false);
  useEffect(() => {
    let active = true;
    setLoading(true); setEmail(''); setError('');
    setCanTrustBrowser(false); setTrustBrowser(false);
    // Read-only preview: opening/scanning an email never consumes the challenge.
    void api.previewAccountMagicLink(token).then(result => {
      if (active) { setEmail(result.email); setCanTrustBrowser(Boolean(result.canTrustBrowser)); }
    }).catch(reason => {
      if (active) setError(reason instanceof ApiError ? reason.message : 'Unable to check this sign-in link. Return to sign in and request a new email.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);
  const mismatch = Boolean(email && currentUser && currentUser.email.toLowerCase() !== email.toLowerCase());
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!email || mismatch || busy) return;
    setBusy(true); setError('');
    try { onAuthenticated(await api.verifyAccountMagicLink(token, ...(trustBrowser ? [true] as const : []))); }
    catch (reason) { setError(reason instanceof ApiError ? reason.message : 'Unable to sign in. Return to sign in and request a new email.'); }
    finally { setBusy(false); }
  }
  return <main className="login-shell">
    <section className="login-panel" aria-labelledby="magic-sign-in-title">
      <Brand className="brand--login" />
      <div className="login-heading"><span className="eyebrow">Account security</span><h1 id="magic-sign-in-title">Confirm sign-in</h1><p>Check the account below before signing in on this browser.</p></div>
      <form className="login-form" onSubmit={submit}>
        {loading && <p role="status">Checking your sign-in link…</p>}
        {email && <label>Account email<input type="email" value={email} readOnly /></label>}
        {(error || mismatch) && <div className="form-alert" role="alert"><Icon name="warning" size={18} /><span>{mismatch ? 'A different account is already signed in. Return to your dashboard and sign out before reopening this link.' : error}</span></div>}
        {browserTrustEnabled && canTrustBrowser && !mismatch && <label className="checkbox-field"><input className="control-choice" type="checkbox" checked={trustBrowser} disabled={busy} onChange={event => setTrustBrowser(event.target.checked)} /><span><strong>Trust this browser for 30 days</strong><small>Skip email verification on password sign-in. Do not use on a shared computer.</small></span></label>}
        {email && !mismatch && <button className="button button--primary button--large" type="submit" disabled={busy || loading}>{busy ? <><span className="spinner" />Signing in…</> : 'Sign in'}</button>}
        <button className="text-button login-forgot" type="button" onClick={onCancel} disabled={busy}>{currentUser ? 'Return to dashboard' : 'Return to sign in'}</button>
      </form>
      <p className="login-security">The code and link share a 10-minute expiry. Using either invalidates both.</p>
      <BrandLinks />
    </section>
    <aside className="login-art" aria-hidden="true"><img className="login-art__image" src={getBranding().assets.loginIllustration} alt="" /></aside>
  </main>;
}
